import React, { useMemo, useRef, useState } from "react";
import Webcam from "react-webcam";
import { useTranslation } from "react-i18next";
import gateway from "./rekognitionGateway";

const DEFAULT_THRESHOLD = 85;

const IconUpload = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-4 w-4"
    {...props}
  >
    <path d="M4 17v2.5A1.5 1.5 0 0 0 5.5 21h13a1.5 1.5 0 0 0 1.5-1.5V17" />
    <path d="M12 3v12" />
    <path d="m8 7 4-4 4 4" />
  </svg>
);

const IconCamera = (props) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="h-4 w-4"
    {...props}
  >
    <path d="M4 7h3l2-3h6l2 3h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" />
    <path d="M12 13a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z" />
  </svg>
);

const toBase64Object = (file, dataUrl) => ({
  name: file?.name || "",
  type: file?.type || "",
  size: file?.size || 0,
  dataUrl,
  base64: dataUrl?.split(",")[1] || "",
  capturedAt: Date.now()
});

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

function IdentityVerificationModal({
  isOpen,
  onClose,
  onSuccess,
  signer,
  documentId,
  verificationConfig = {}
}) {
  const { t } = useTranslation();
  const webcamRef = useRef(null);
  const documentWebcamRef = useRef(null);
  const [documentFront, setDocumentFront] = useState(null);
  const [documentBack, setDocumentBack] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState('privacy');
  const [result, setResult] = useState(null);
  const [documentCameraSide, setDocumentCameraSide] = useState(null);

  const requireDocument = true;
  const threshold = verificationConfig.threshold || DEFAULT_THRESHOLD;

  const signerLabel = useMemo(() => {
    if (!signer) return '';
    return signer.email ? `${signer.name} (${signer.email})` : signer.name;
  }, [signer]);

  if (!isOpen) {
    return null;
  }

  const handleFileUpload = async (event, setter) => {
    const file = event?.target?.files?.[0];
    if (!file) return;
    const fileType = (file.type || '').toLowerCase();
    const extension = (file.name || '').split('.').pop()?.toLowerCase();
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    const allowedExtensions = ['png', 'jpg', 'jpeg'];
    const isAllowed =
      allowedTypes.includes(fileType) || (extension && allowedExtensions.includes(extension));
    if (!isAllowed) {
      setStatus('error');
      setMessage(t('identity-verification-invalid-file'));
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setter(toBase64Object(file, dataUrl));
      setStatus('idle');
      setMessage('');
    } catch (err) {
      console.error('Error al leer el archivo', err);
      setStatus('error');
      setMessage(t('identity-verification-file-error'));
    }
  };

  const openDocumentCamera = (side) => {
    setDocumentCameraSide(side);
    setStatus('idle');
    setMessage('');
  };

  const closeDocumentCamera = () => {
    setDocumentCameraSide(null);
  };

  const captureDocumentPhoto = () => {
    if (!documentCameraSide || !documentWebcamRef.current) {
      setStatus('error');
      setMessage(t('identity-verification-camera-permission'));
      return;
    }
    const dataUrl = documentWebcamRef.current.getScreenshot();
    if (!dataUrl) {
      setStatus('error');
      setMessage(t('identity-verification-camera-permission'));
      return;
    }
    const capturedDocument = toBase64Object(
      { name: `document-${documentCameraSide}.jpg`, type: 'image/jpeg' },
      dataUrl
    );
    if (documentCameraSide === 'front') {
      setDocumentFront(capturedDocument);
    } else {
      setDocumentBack(capturedDocument);
    }
    setStatus('idle');
    setMessage('');
    setDocumentCameraSide(null);
  };

  const captureSelfie = () => {
    if (!webcamRef.current) {
      setStatus('error');
      setMessage(t('identity-verification-camera-permission'));
      return null;
    }
    const dataUrl = webcamRef.current.getScreenshot();
    if (!dataUrl) {
      setStatus('error');
      setMessage(t('identity-verification-camera-permission'));
      return null;
    }
    const capturedSelfie = toBase64Object({ name: 'selfie.jpg', type: 'image/jpeg' }, dataUrl);
    setSelfie(capturedSelfie);
    setStatus('idle');
    setMessage('');
    return capturedSelfie;
  };

  const goToNextStep = () => {
    if (step === 'privacy') {
      setStep('document');
    } else if (step === 'document') {
      if (requireDocument && !documentFront?.base64) {
        setStatus('error');
        setMessage(t('identity-verification-missing-document'));
        return;
      }
      setStatus('idle');
      setMessage('');
      setStep('selfie');
    }
  };

  const handleVerify = async (capturedSelfie) => {
    const activeSelfie = capturedSelfie || selfie;
    if (!activeSelfie?.base64) {
      setStatus('error');
      setMessage(t('identity-verification-missing-selfie'));
      return;
    }
    const documentImage = documentFront?.base64 || documentBack?.base64 || '';
    if (!documentImage) {
      setStatus('error');
      setMessage(t('identity-verification-missing-document'));
      return;
    }

    setIsSubmitting(true);
    setStatus('processing');
    setMessage(t('identity-verification-processing'));

    try {
      const comparison = await gateway.compareFaces(
        documentImage,
        activeSelfie.base64,
        threshold
      );
      const bestMatch = comparison?.FaceMatches?.length
        ? [...comparison.FaceMatches].sort((a, b) => b.Similarity - a.Similarity)[0]
        : null;
      const similarity = bestMatch?.Similarity || 0;
      const isValid = similarity >= threshold;

      const payload = {
        similarity,
        matchedPerson: bestMatch,
        comparison,
        selfie: activeSelfie,
        documentFront,
        documentBack,
        signer,
        documentId,
        timestamp: Date.now()
      };

      setResult({ isValid, similarity, payload });
      setStep('result');
      setStatus(isValid ? 'success' : 'error');
      setMessage(
        isValid
          ? t('identity-verification-success', { value: Math.round(similarity) })
          : t('identity-verification-low-score', { value: Math.round(similarity) })
      );
    } catch (error) {
      console.error('Error verificando identidad', error);
      setStatus('error');
      setMessage(t('identity-verification-unexpected'));
      setResult({ isValid: false, similarity: 0 });
      setStep('result');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCaptureAndValidate = async () => {
    const captured = captureSelfie();
    if (!captured) return;
    await handleVerify(captured);
  };

  const renderPrivacy = () => (
    <div className="space-y-4">
      <p className="text-sm text-base-content/70">
        {t('identity-verification-privacy')}
      </p>
      <button
        type="button"
        className="op-btn op-btn-primary w-full"
        onClick={goToNextStep}
      >
        {t('identity-verification-verify-button')}
      </button>
    </div>
  );

  const renderDocumentCamera = () => {
    if (!documentCameraSide) return null;
    const sideLabel =
      documentCameraSide === 'front'
        ? t('identity-verification-camera-front')
        : t('identity-verification-camera-back');
    return (
      <div className="space-y-3 rounded border border-dashed border-base-300 p-4">
        <p className="text-sm text-base-content/70">
          {t('identity-verification-camera-instructions', { side: sideLabel })}
        </p>
        <div className="relative rounded border border-base-300 overflow-hidden">
          <Webcam
            ref={documentWebcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            className="w-full aspect-video object-cover"
          />
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            className="op-btn op-btn-primary flex-1"
            onClick={captureDocumentPhoto}
          >
            {t('identity-verification-camera-capture')}
          </button>
          <button
            type="button"
            className="op-btn op-btn-ghost flex-1"
            onClick={closeDocumentCamera}
          >
            {t('cancel')}
          </button>
        </div>
      </div>
    );
  };

  const renderDocumentStep = () => (
    <>
      <div className="space-y-4">
        <h3 className="font-semibold text-base-content">
          {t('identity-verification-document-section')}
        </h3>
        <p className="text-sm text-base-content/70">
          {t('identity-verification-document-help')}
        </p>
        <div className="space-y-2">
          <p className="text-sm font-semibold text-base-content">
            {t('identity-verification-front-title')}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <label
              htmlFor="identity-document-front"
              className="op-btn op-btn-outline op-btn-sm w-full flex items-center justify-center gap-2"
            >
              <IconUpload />
              {t('identity-verification-upload-front')}
            </label>
            <button
              type="button"
              className="op-btn op-btn-secondary op-btn-sm w-full flex items-center justify-center gap-2"
              onClick={() => openDocumentCamera('front')}
            >
              <IconCamera />
              {t('identity-verification-open-camera-front')}
            </button>
          </div>
        </div>
        <input
          id="identity-document-front"
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={(event) => handleFileUpload(event, setDocumentFront)}
        />
        {documentFront && (
          documentFront.type.includes('pdf') ? (
            <div className="rounded border border-base-300 p-3 text-sm">
              <p className="font-semibold truncate">{documentFront.name}</p>
              <p className="text-xs text-base-content/70">
                PDF · {Math.round((documentFront.size || 0) / 1024)} KB
              </p>
            </div>
          ) : (
            <img
              src={documentFront.dataUrl}
              alt="document front"
              className="w-full rounded border border-base-300"
            />
          )
        )}
        <div className="space-y-2">
          <p className="text-sm font-semibold text-base-content">
            {t('identity-verification-back-title')}
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <label
              htmlFor="identity-document-back"
              className="op-btn op-btn-outline op-btn-sm w-full flex items-center justify-center gap-2"
            >
              <IconUpload />
              {t('identity-verification-upload-back')}
            </label>
            <button
              type="button"
              className="op-btn op-btn-secondary op-btn-sm w-full flex items-center justify-center gap-2"
              onClick={() => openDocumentCamera('back')}
            >
              <IconCamera />
              {t('identity-verification-open-camera-back')}
            </button>
          </div>
        </div>
        <input
          id="identity-document-back"
          type="file"
          accept="image/png,image/jpeg"
          className="hidden"
          onChange={(event) => handleFileUpload(event, setDocumentBack)}
        />
        {documentBack && (
          documentBack.type.includes('pdf') ? (
            <div className="rounded border border-base-300 p-3 text-sm">
              <p className="font-semibold truncate">{documentBack.name}</p>
              <p className="text-xs text-base-content/70">
                PDF · {Math.round((documentBack.size || 0) / 1024)} KB
              </p>
            </div>
          ) : (
            <img
              src={documentBack.dataUrl}
              alt="document back"
              className="w-full rounded border border-base-300"
            />
          )
        )}
        {renderDocumentCamera()}
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          className="op-btn op-btn-primary"
          onClick={goToNextStep}
        >
          {t('identity-verification-verify-button')}
        </button>
      </div>
    </>
  );

  const renderSelfieStep = () => (
    <div className="space-y-4">
      <h3 className="font-semibold text-base-content">
        {t('identity-verification-selfie-section')}
      </h3>
      <p className="text-sm text-base-content/70">
        {t('identity-verification-selfie-help')}
      </p>
      <div className="relative rounded border border-base-300 overflow-hidden">
        <Webcam
          ref={webcamRef}
          audio={false}
          screenshotFormat="image/jpeg"
          mirrored
          className="w-full aspect-video object-cover"
        />
      </div>
      <button
        type="button"
        className="op-btn op-btn-primary w-full"
        onClick={handleCaptureAndValidate}
        disabled={isSubmitting}
      >
        {isSubmitting
          ? t('identity-verification-processing-short')
          : t('identity-verification-validate')}
      </button>
      {message && status !== 'processing' && (
        <div
          className={`rounded border px-4 py-3 text-sm ${
            status === 'success'
              ? 'border-green-300 text-green-800 bg-green-50'
              : status === 'error'
              ? 'border-error text-error'
              : 'border-info text-info'
          }`}
        >
          {message}
        </div>
      )}
    </div>
  );

  const handleContinue = () => {
    if (result?.isValid && result?.payload) {
      onSuccess?.(result.payload);
    } else if (onClose) {
      onClose();
    }
  };

  const renderResult = () => (
    <div className="space-y-4 text-center">
      <div
        className={`text-3xl font-bold ${result?.isValid ? 'text-success' : 'text-error'}`}
      >
        {result?.isValid
          ? t('identity-verification-success-title')
          : t('identity-verification-error-title')}
      </div>
      <p className="text-base-content/70">
        {result?.isValid
          ? t('identity-verification-success-description', {
              value: Math.round(result?.similarity || 0)
            })
          : t('identity-verification-error-description')}
      </p>
      {message && (
        <p className="text-sm text-base-content">{message}</p>
      )}
      <div className="flex flex-col gap-3">
        <button
          type="button"
          className={`op-btn ${result?.isValid ? 'op-btn-primary' : 'op-btn-error'}`}
          onClick={handleContinue}
        >
          {result?.isValid
            ? t('identity-verification-continue')
            : t('identity-verification-contact-support')}
        </button>
        {!result?.isValid && (
          <button
            type="button"
            className="op-btn op-btn-secondary"
            onClick={() => {
              setStep('document');
              setResult(null);
              setStatus('idle');
              setMessage('');
            }}
          >
            {t('identity-verification-capture-selfie')}
          </button>
        )}
      </div>
    </div>
  );
  return (
    <div className="op-modal op-modal-open z-[999]">
      <div className="op-modal-box max-w-4xl w-[95%]">
        <div className="flex flex-col gap-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs uppercase text-primary font-semibold">
                {t('identity-verification-title')}
              </p>
              <h2 className="text-xl font-bold text-base-content">
                {signerLabel || t('identity-verification-signer-placeholder')}
              </h2>
              <p className="text-sm text-base-content/70">
                {t('identity-verification-description')}
              </p>
            </div>
            {onClose && (
              <button
                type="button"
                className="op-btn op-btn-ghost op-btn-sm"
                onClick={onClose}
              >
                {t('cancel')}
              </button>
            )}
          </div>

          {step === 'privacy' && renderPrivacy()}

          {step === 'document' && (
            <div className="grid gap-6">
              {renderDocumentStep()}
            </div>
          )}

          {step === 'selfie' && renderSelfieStep()}

          {step === 'result' && renderResult()}
        </div>
      </div>
    </div>
  );
}

export default IdentityVerificationModal;
